import type { DrawFrame } from "./useCanvasAnimation";

// Canvas strokes take literal colors: the venue's blues, violet and cyan on navy.
const GRID = "rgba(120, 140, 255, 0.14)";
const CYAN = "34, 211, 238";
const BLUE = "79, 107, 255";
const VIOLET = "139, 92, 246";
const WHITE = "rgba(255, 255, 255, 0.95)";

/** Time for the growth curve to draw itself. */
const GROWTH_DRAW_MS = 3500;
const CURVE_POINTS = 90;

/** « Croissance »: a perspective grid scrolling slowly and a curve drawing itself. */
const growth: DrawFrame = (context, width, height, elapsed) => {
  const horizon = height * 0.45;
  context.strokeStyle = GRID;
  context.lineWidth = 1;
  const shift = (elapsed * 0.012) % 40;
  for (let i = -1; i < 14; i++) {
    const y = horizon + Math.pow((i * 40 + shift) / 560, 1.6) * height * 0.65;
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
  }
  for (let i = -14; i <= 14; i++) {
    context.beginPath();
    context.moveTo(width / 2 + i * 30, horizon);
    context.lineTo(width / 2 + i * 160, height);
    context.stroke();
  }

  // Eased, so the curve slows down as it reaches the top.
  const linear = Math.min(elapsed / GROWTH_DRAW_MS, 1);
  const progress = 1 - Math.pow(1 - linear, 3);
  const count = Math.max(1, Math.round(progress * CURVE_POINTS));
  const pointAt = (index: number) => {
    const u = index / CURVE_POINTS;
    const rise = u * u * 0.5 + 0.05 * Math.sin(u * 12) + 0.08;
    return [width * (0.08 + u * 0.84), height * (0.88 - rise)];
  };
  context.save();
  context.strokeStyle = `rgba(${CYAN}, 0.95)`;
  context.lineWidth = 3;
  context.shadowColor = `rgba(${CYAN}, 0.6)`;
  context.shadowBlur = 16;
  context.beginPath();
  for (let i = 0; i <= count; i++) {
    const [x, y] = pointAt(i);
    if (i === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  }
  context.stroke();
  const [x, y] = pointAt(count);
  context.fillStyle = WHITE;
  context.beginPath();
  context.arc(x, y, 4, 0, Math.PI * 2);
  context.fill();
  context.restore();
};

/** « Aurore »: three soft halos drifting slowly. */
const aurora: DrawFrame = (context, width, height, elapsed) => {
  const halos: [string, number, number][] = [
    [BLUE, 0.00025, 0.3],
    [VIOLET, 0.00018, 0.6],
    [CYAN, 0.0002, 0.9],
  ];
  halos.forEach(([color, speed, offset], index) => {
    const cx = width * (0.5 + 0.35 * Math.sin(elapsed * speed + offset * 5));
    const cy = height * (0.5 + 0.3 * Math.cos(elapsed * speed * 1.3 + index));
    const radius = Math.max(width, height) * 0.45;
    const gradient = context.createRadialGradient(cx, cy, 0, cx, cy, radius);
    gradient.addColorStop(0, `rgba(${color}, 0.5)`);
    gradient.addColorStop(1, `rgba(${color}, 0)`);
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
  });
};

/** « Faisceaux »: light streaks running up diagonal rails. */
const beams: DrawFrame = (context, width, height, elapsed) => {
  const rails = Math.ceil(width / 140) + 4;
  for (let i = 0; i < rails; i++) {
    const offset = i * 140 - height * 0.6;
    context.strokeStyle = `rgba(${BLUE}, 0.12)`;
    context.lineWidth = 1.5;
    context.beginPath();
    context.moveTo(offset, height);
    context.lineTo(offset + height * 0.9, 0);
    context.stroke();
    const position = ((elapsed * 0.00012 + i * 0.17) % 1.4) - 0.2;
    const x = offset + height * 0.9 * position;
    const y = height - height * position;
    const gradient = context.createLinearGradient(x - 120, y + 120, x, y);
    gradient.addColorStop(0, `rgba(${CYAN}, 0)`);
    gradient.addColorStop(1, "rgba(196, 181, 253, 0.9)");
    context.strokeStyle = gradient;
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(x - 120, y + 120);
    context.lineTo(x, y);
    context.stroke();
  }
};

/** Fixed pseudo-random dots, so the network looks the same at every start. */
const DOTS = Array.from({ length: 60 }, (_, index) => {
  const random = (seed: number) => {
    const value = Math.sin(index * 12.9898 + seed * 78.233) * 43758.5453;
    return value - Math.floor(value);
  };
  return {
    x: random(1),
    y: random(2),
    vx: (random(3) - 0.5) * 0.00004,
    vy: (random(4) - 0.5) * 0.00004,
  };
});

/** Back and forth between 0 and 1, so dots bounce off the edges. */
const bounce = (value: number) => {
  const cycle = ((value % 2) + 2) % 2;
  return cycle > 1 ? 2 - cycle : cycle;
};

/** « Constellation »: leads as dots, linked when they come close. */
const constellation: DrawFrame = (context, width, height, elapsed) => {
  const points = DOTS.map((dot) => ({
    x: bounce(dot.x + dot.vx * elapsed) * width,
    y: bounce(dot.y + dot.vy * elapsed) * height,
  }));
  const reach = Math.min(width, height) * 0.18;
  context.lineWidth = 1;
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const distance = Math.hypot(
        points[i].x - points[j].x,
        points[i].y - points[j].y,
      );
      if (distance < reach) {
        context.strokeStyle = `rgba(140, 160, 255, ${(1 - distance / reach) * 0.45})`;
        context.beginPath();
        context.moveTo(points[i].x, points[i].y);
        context.lineTo(points[j].x, points[j].y);
        context.stroke();
      }
    }
  }
  context.fillStyle = WHITE;
  points.forEach(({ x, y }) => {
    context.beginPath();
    context.arc(x, y, 2.5, 0, Math.PI * 2);
    context.fill();
  });
};

/** The welcome screen's backgrounds; « faisceaux » is the one in use. */
export const WELCOME_BACKGROUNDS = {
  croissance: growth,
  aurore: aurora,
  faisceaux: beams,
  constellation,
} satisfies Record<string, DrawFrame>;

export type WelcomeBackgroundName = keyof typeof WELCOME_BACKGROUNDS;

export const DEFAULT_WELCOME_BACKGROUND: WelcomeBackgroundName = "faisceaux";
